import { Grid, Typography } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useLayoutContext } from '../../../Context/Layout.context';
import usePermission, { UserPermission } from '../../../hooks/usePermission';
import {
  deleteSpecialCasesDefinition,
  getSpecialCasesDefinitions,
} from '../../../services/admin/settings/systemSettings.service';
import ActionsMenu from '../../ActionsMenu/ActionsMenu.component';
import { ActionMenuItem } from '../../ActionsMenu/ActionsMenu.types';
import { ButtonOne } from '../../ButtonOne/ButtonOne.component';
import { ItemsFilter } from '../../SelectableTable/filters/ItemsFilter.component';
import { SelectableTable } from '../../SelectableTable/SelectableTable.component';
import { IndexedStrings, SelectableTableColumn } from '../../SelectableTable/SelectableTable.types';
import { RecipientsChips } from './Recipients';
import { EditSpecialCase } from './EditSpecialCase.component';
import { EmptySpecialCaseTable } from './EmptySpecialCaseTable.component';
import { useStyles } from './SpecialCases.styles';
import {
  SpecialCase,
  SpecialCaseFilter,
  SpecialCaseFilterElements,
  SpecialCaseResult,
  SpecialCasesSortBy,
  SpecialCasesTableItem,
  WorkMode,
} from './SpecialCases.types';
import { Prompt } from '../../Prompt/Prompt.component';
import _ from 'lodash';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';

interface SpecialCasesProps {
  onClose: () => void;
  onChange: () => void;
}

const defaultFilter = {
  title: '',
  description: '',
  recipients: [],
  active: [true],
};

export const SpecialCases: React.FC<SpecialCasesProps> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { showLoader, openAlert } = useLayoutContext();

  const [data, setData] = useState<SpecialCasesTableItem[]>([]);
  const [filter, setFilter] = useState<SpecialCaseFilter>(defaultFilter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [sortByColumnId, setSortByColumnId] = useState<SpecialCasesSortBy>('title');
  const [isDescending, setIsDescending] = useState<boolean>(true);
  const [isEditSpecialCaseModalOpen, setIsEditSpecialCaseModalOpen] = useState<boolean>(false);
  const [isDeleteSpecialCaseModalOpen, setIsDeleteSpecialCaseModalOpen] = useState<boolean>(false);
  const [isErrorDeleteSpecialCaseModalOpen, setIsErrorDeleteSpecialCaseModalOpen] = useState<boolean>(false);
  const [selectedSpecialCase, setSelectedSpecialCase] = useState<SpecialCase>();
  const [workMode, setWorkMode] = useState<WorkMode>(WorkMode.EDIT);
  const [noSpecialCases, setNoSpecialCases] = useState<boolean>(false);

  const hasAdminSettingsManagePermission = usePermission(UserPermission.AdminSettingsManage);

  const columns: SelectableTableColumn[] = [
    {
      id: 'title',
      type: 'text',
      title: t('systemSettings:specialCases.table.title'),
      filterType: 'search',
    },
    {
      id: 'description',
      type: 'text',
      title: t('systemSettings:specialCases.table.description'),
      filterType: 'search',
    },
    {
      id: 'recipients',
      type: 'text',
      title: t('systemSettings:specialCases.table.recipients'),
      filterType: 'filter',
    },
    {
      id: 'actions',
      type: 'actions',
      title: 'Akcje',
      filterType: null,
      skipEnterMethod: true,
    },
  ];

  const getActionsMenuIcon = (row: SpecialCase) => {
    const items: ActionMenuItem[] = [];
    if (hasAdminSettingsManagePermission) {
      items.push({
        text: 'Edytuj szczególny przypadek',
        onClick: () => {
          setSelectedSpecialCase((prevState) => ({
            ...prevState,
            id: row.id,
            title: row.title,
            description: row.description,
            domesticCoordinator: row.domesticCoordinator,
            examTaskGroupCoordinator: row.examTaskGroupCoordinator,
            examiningTeamChairman: row.examiningTeamChairman,
            techAdmin: row.techAdmin,
          }));
          setWorkMode(WorkMode.EDIT);
          setIsEditSpecialCaseModalOpen(true);
        },
      });
      items.push({
        text: 'Usuń szczególny przypadek',
        onClick: () => {
          setSelectedSpecialCase((prevState) => ({
            ...prevState,
            id: row.id,
            title: row.title,
            description: row.description,
            domesticCoordinator: row.domesticCoordinator,
            examTaskGroupCoordinator: row.examTaskGroupCoordinator,
            examiningTeamChairman: row.examiningTeamChairman,
            techAdmin: row.techAdmin,
          }));
          setIsDeleteSpecialCaseModalOpen(true);
        },
      });
    }
    return <ActionsMenu menuItems={items} />;
  };

  const isWithoutEmptyMessage = () => {
    return !(!data.length && Object.entries(filter).toString() !== Object.entries(defaultFilter).toString());
  };

  const refreshData = _.debounce((filter: SpecialCaseFilter, signal?: AbortSignal) => {
    if (!isValidFilter) return;
    showLoader(true);
    getSpecialCasesDefinitions(filter, sortByColumnId, isDescending, signal)
      .then(
        (specialCases: SpecialCase[]) => {
          if (specialCases) {
            const tableData: SpecialCasesTableItem[] = specialCases.map((row: SpecialCase) => ({
              id: row.id,
              title: row.title,
              description: row.description,
              recipients: (
                <RecipientsChips
                  domesticCoordinator={row.domesticCoordinator}
                  examTaskGroupCoordinator={row.examTaskGroupCoordinator}
                  examiningTeamChairman={row.examiningTeamChairman}
                  techAdmin={row.techAdmin}
                />
              ),
              actions: getActionsMenuIcon(row),
            }));

            if (
              !specialCases.length &&
              Object.entries(filter).toString() === Object.entries(defaultFilter).toString()
            ) {
              setNoSpecialCases(true);
            } else setNoSpecialCases(false);
            setData(tableData);
          }
        },
        () => {
          openAlert('get_error');
        },
      )
      .finally(() => {
        showLoader(false);
      });
  });

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    refreshData(filter, signal);
  }, [filter, isDescending, sortByColumnId]);

  const handleDeleteSpecialCases = () => {
    showLoader(true);
    if (selectedSpecialCase) {
      deleteSpecialCasesDefinition(selectedSpecialCase.id)
        .then(
          () => {
            openAlert('delete_success');
            refreshData(filter);
          },
          (error) => {
            if (error.response.data === SpecialCaseResult.AssessmentSessionIsOpen) {
              setIsErrorDeleteSpecialCaseModalOpen(true);
            }
            openAlert('delete_error');
          },
        )
        .finally(() => {
          showLoader(false);
          setIsDeleteSpecialCaseModalOpen(false);
        });
    } else return;
  };

  const handleOnClose = () => {
    setIsEditSpecialCaseModalOpen(false);
    refreshData(filter);
  };

  const handleFilterChange = (name: string, value: unknown) => {
    typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
    const newFilterAttribute: Partial<SpecialCaseFilter> = { [name]: Array.isArray(value) ? value : [value] };
    setFilter((prevState) => {
      return {
        ...prevState,
        ...newFilterAttribute,
      };
    });
  };

  const filterElements: SpecialCaseFilterElements = {
    recipients: (
      <ItemsFilter<string>
        valueName="recipients"
        valueLabel="Adresaci"
        onChange={handleFilterChange}
        resetFilter={!filter.recipients.length}
        itemsWithValues={[
          { label: 'KK', value: 'domesticCoordinator' },
          { label: 'KGZ', value: 'examTaskGroupCoordinator' },
          { label: 'PZE', value: 'examiningTeamChairman' },
          { label: 'Administrator Techniczny', value: 'techAdmin' },
        ]}
        filter={filter.recipients}
      />
    ),
  };

  const handleOpenAddNewSpecialCase = () => {
    setWorkMode(WorkMode.ADD);
    setSelectedSpecialCase({
      id: '',
      title: '',
      description: '',
      domesticCoordinator: false,
      examTaskGroupCoordinator: false,
      examiningTeamChairman: false,
      techAdmin: false,
    });
    setIsEditSpecialCaseModalOpen(true);
  };

  const filterTranslated: IndexedStrings = useMemo(() => {
    const result: IndexedStrings = {};
    Object.keys(filter).forEach((key: string) => {
      result[key] = filter[key as keyof SpecialCaseFilter]?.toString() ?? '';
    });
    return result;
  }, [filter]);

  const filterElementsTranslated: { [key: string]: JSX.Element } = {};
  Object.keys(filterElements).forEach((key: string) => {
    filterElementsTranslated[key] = filterElements[key as keyof SpecialCaseFilterElements];
  });

  return (
    <>
      <Grid item xs={12}>
        <Grid item xs={12} className={classes.headerRow}>
          <Grid item xs={6}>
            <Typography className={classes.tabTitle}>Szczególne przypadki</Typography>
          </Grid>
          {!noSpecialCases && (
            <Grid item xs={6} className={classes.headerButton}>
              <ButtonOne
                style={{ backgroundColor: '#3C419A' }}
                variant="contained"
                color="secondary"
                onClick={handleOpenAddNewSpecialCase}
              >
                + DODAJ NOWY SZCZEGÓLNY PRZYPADEK
              </ButtonOne>
            </Grid>
          )}
        </Grid>
        {noSpecialCases ? (
          <Grid item xs={12} style={{ marginTop: '70px' }}>
            <EmptySpecialCaseTable
              onClose={() => {
                refreshData(filter);
              }}
            />
          </Grid>
        ) : (
          <>
            <div style={{ width: '100%', height: '450px', overflowY: 'auto' }}>
              <SelectableTable<SpecialCasesTableItem, SpecialCasesSortBy>
                columns={columns}
                rows={data}
                filter={filterTranslated}
                filterElements={filterElementsTranslated}
                onFilterChange={handleFilterChange}
                sortByProperty={sortByColumnId}
                isDescending={isDescending}
                setIsDescending={setIsDescending}
                setSortByColumn={setSortByColumnId}
                withoutEmptyMessage={isWithoutEmptyMessage()}
                caption="Szczególne przypadki"
              />
            </div>
            {isEditSpecialCaseModalOpen && (
              <EditSpecialCase
                title={workMode === WorkMode.EDIT ? 'Edytuj szczególny przypadek' : 'Dodaj nowy szczególny przypadek'}
                data={
                  selectedSpecialCase
                    ? selectedSpecialCase
                    : {
                        id: '',
                        title: '',
                        description: '',
                        domesticCoordinator: false,
                        examTaskGroupCoordinator: false,
                        examiningTeamChairman: false,
                        techAdmin: false,
                      }
                }
                workMode={workMode}
                onCancel={() => {
                  setIsEditSpecialCaseModalOpen(false);
                }}
                onClose={handleOnClose}
              />
            )}
            {isDeleteSpecialCaseModalOpen && (
              <Prompt
                type="confirmation"
                mainActionButtonText="TAK"
                open={true}
                mainText="Czy na pewno chcesz usunąć szczególny przypadek?"
                onConfirm={() => handleDeleteSpecialCases()}
                onCancel={() => setIsDeleteSpecialCaseModalOpen(false)}
              />
            )}
            {isErrorDeleteSpecialCaseModalOpen && (
              <Prompt
                type="warning"
                mainActionButtonText="OK"
                open={true}
                mainText="Nie można usunąć szczególnego przypadku, ponieważ przynajmniej jedna sesja w systemie ma status: otwarta."
                onConfirm={() => setIsErrorDeleteSpecialCaseModalOpen(false)}
              />
            )}
          </>
        )}
      </Grid>
    </>
  );
};
